import type { ChatResponseAdapterData, KhRepoKnowledgeRepositoryEnvelope } from "../models/kh-repo.models";
import type { ApiEnvelope, WsEnvelope } from "../models/uwhc.models";
import { ASSISTANT_CHAT_LIMITS } from "../uwhc.constants";

export function formatMessageTime(ts: number) {
  return new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function buildMessageWithInitialContext(question: string, initialUserMessageContext?: string) {
  const trimmedContext = initialUserMessageContext?.trim();

  if (!trimmedContext) {
    return question;
  }

  return `${trimmedContext}\n\nUser message:\n${question}`;
}

// Check the complete first question, including voyage context, without silently removing either part.
export function getAssistantQuestionError(message: string, envelope: string): string | null {
  if (message.length > ASSISTANT_CHAT_LIMITS.questionCharacters) {
    return "Your question and voyage context exceed the 20,000-character limit. Shorten your question and try again.";
  }

  if (new TextEncoder().encode(envelope).byteLength > ASSISTANT_CHAT_LIMITS.clientPayloadBytes) {
    return "Your question is too large to send. Shorten it and try again.";
  }

  return null;
}

// === FEATURE ===

export function normalizeApi(r: ApiEnvelope | null | undefined): ChatResponseAdapterData {
  const rows = r?.uwhc_providers?.db_response ?? r?.hull_cleaning?.uwhc_providers?.db_response ?? [];
  const sql = r?.uwhc_providers?.sql_query ?? r?.hull_cleaning?.uwhc_providers?.sql_query ?? "";
  const message = r?.message ?? r?.uwhc_providers?.message ?? r?.hull_cleaning?.uwhc_providers?.message ?? "";
  return {
    text:
      message ||
      (rows?.length
        ? `I found ${rows.length} entr${rows.length === 1 ? "y" : "ies"}.`
        : "I could not find matching results."),
    data: { rows, sql },
  };
}

export function normalizeKnowledgeRepositoryApi(
  r: KhRepoKnowledgeRepositoryEnvelope | null | undefined,
): ChatResponseAdapterData {
  const rawKnowledgeItems = r?.knowledge_repository?.items_info ?? [];
  const message = r?.message ?? r?.knowledge_repository?.message ?? "";

  return {
    text:
      message ||
      (rawKnowledgeItems?.length
        ? `I found ${rawKnowledgeItems.length} knowledge source${rawKnowledgeItems.length === 1 ? "" : "s"}.`
        : "I could not find matching results."),
    data: { rawKnowledgeItems },
  };
}

export const buildWsEnvelope = <TPayload>(message_type: string, payload: TPayload) =>
  JSON.stringify({ message_type, payload } satisfies WsEnvelope<TPayload>);

export const unwrapWsEnvelope = (raw: any): { message_type?: string; payload: any } => {
  if (raw && typeof raw === "object" && "message_type" in raw && "payload" in raw) {
    return {
      message_type: typeof raw.message_type === "string" ? raw.message_type : undefined,
      payload: raw.payload,
    };
  }
  return { payload: raw };
};
